import { v7 as uuidv7 } from "uuid";
import * as E from "fp-ts/Either";
import type { TaskEitherTs } from "@ps-bridge/interop";
import API_URL from "@utils/config";
import { taskEitherChain, taskEitherLeft, taskEitherOf, taskEitherTryCatch } from "@utils/effects/branded";
import type {
    PageCounterClient,
    PageCounterClientError,
    PageCounterRequest,
    PageCounterResult,
} from "@services/clients/PageCounterClient";
import { PageCounterRoutes } from "@services/clients/PageCounterRoutes";
import { decodePageCounterResult, toPageCounterError } from "@services/clients/PageCounterClient";

type PageCounterRequestWire = {
    readonly project_id: string;
    readonly document_id: string;
};

const networkError = (): PageCounterClientError =>
    toPageCounterError("The page counter service could not be reached.", "networkError");

const responseDecodeError = (): PageCounterClientError =>
    toPageCounterError("Unable to decode page counter response.", "responseDecodeError");

const serviceUnavailableError = (): PageCounterClientError =>
    toPageCounterError("The page counter service is unavailable.", "serviceUnavailable");

const statusError = (status: number): PageCounterClientError => {
    if (status === 503) {
        return serviceUnavailableError();
    }

    if (status >= 400 && status < 500) {
        return toPageCounterError("The page counter request was rejected.", "invalidRequest");
    }

    return serviceUnavailableError();
};

const encodeRequest = (request: PageCounterRequest): PageCounterRequestWire => ({
    project_id: request.projectId,
    document_id: request.documentId,
});

export class DefaultPageCounterClient implements PageCounterClient {
    countUploadedDocument(request: PageCounterRequest): TaskEitherTs<PageCounterClientError, PageCounterResult> {
        const fetchTask = taskEitherTryCatch(
            () =>
                fetch(`${API_URL}${PageCounterRoutes.countUploadedDocument.replace("{projectId}", request.projectId)}`, {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        "X-Trace-ID": uuidv7(),
                    },
                    body: JSON.stringify(encodeRequest(request)),
                }),
            () => networkError(),
        );

        return taskEitherChain(fetchTask, (response) => {
            if (!response.ok) {
                return taskEitherLeft(statusError(response.status));
            }

            const payloadTask = taskEitherTryCatch(
                () => response.json(),
                () => responseDecodeError(),
            );

            return taskEitherChain(payloadTask, (payload) => {
                const decoded = decodePageCounterResult(payload);
                return E.isLeft(decoded) ? taskEitherLeft(responseDecodeError()) : taskEitherOf(decoded.right);
            });
        });
    }
}
